import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useStore from '../../store/useStore';
import CategorySidebar from '../../components/Products/CategorySidebar';
import ProductCard from '../../components/Products/ProductCard';
import Loader from '../../components/Loader';
import { FaSearch } from 'react-icons/fa';

const ProductsPage = () => {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const {
    getFilteredProducts,
    fetchProducts,
    selectedCategory,
    setSelectedCategory,
    setSearchQuery,
    selectedCategoryData,
    categories,
    fetchCategories,
    products_loading,
    products_loaded
  } = useStore();
  const [sortBy, setSortBy] = useState('default');
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [visibleCount, setVisibleCount] = useState(12);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const queryString = searchParams.toString();

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [fetchCategories, fetchProducts]);

  // Keep URL params as the source of truth for category and text search.
  useEffect(() => {
    const params = new URLSearchParams(queryString);
    const qCategoryId  = params.get('category_id');
    const qSearch      = params.get('search') ?? params.get('q') ?? '';

    if (categoryId) {
      setSelectedCategory(categoryId);
    } else if (qCategoryId) {
      setSelectedCategory(qCategoryId);
    } else {
      setSelectedCategory('all');
    }

    setSearchQuery(qSearch);
    setVisibleCount(12);
  }, [categoryId, queryString, setSelectedCategory, setSearchQuery]);

  const products = getFilteredProducts();

  const filteredProducts = products.filter(
    p => p.price >= priceRange[0] && p.price <= priceRange[1]
  );
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    if (sortBy === 'discount') return b.discount - a.discount;
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    return 0;
  });

  const visibleProducts = sortedProducts.slice(0, visibleCount);

  const getCategoryName = () => {
    if (selectedCategory === 'all' || !selectedCategory) return 'All Products';
    if (selectedCategoryData) return selectedCategoryData.name;

    // Fallback to categories list
    const category = categories.find(cat => cat.category_id.toString() === selectedCategory);
    return category ? category.name : 'Products';
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{getCategoryName()}</h1>
          <p className="text-gray-600 mt-2">{sortedProducts.length} products found</p>
        </div>

        <div className="mb-4 rounded-2xl bg-white p-3 shadow-sm sm:hidden">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileFiltersOpen((prev) => !prev)}
              className="flex min-w-[108px] items-center justify-center gap-2 rounded-xl border border-[#d6e3d7] bg-[#f4f8f4] px-3 py-2.5 text-sm font-semibold text-[#245d3f]"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 12h12m-9 8h6" />
              </svg>
              Filters
            </button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="min-w-0 flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E7B52]"
            >
              <option value="default">Sort</option>
              <option value="name">Name A-Z</option>
              <option value="price-low">Price low-high</option>
              <option value="price-high">Price high-low</option>
              <option value="discount">Best discount</option>
            </select>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <span>{sortedProducts.length} products</span>
            <span>Up to Rs {priceRange[1]}</span>
          </div>
        </div>

        {mobileFiltersOpen && (
          <div className="mb-5 sm:hidden">
            <CategorySidebar
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              mobileMode
              onClose={() => setMobileFiltersOpen(false)}
            />
          </div>
        )}

        <div className="flex flex-col gap-8 lg:flex-row">
          <aside className="hidden lg:block lg:w-1/4">
            <CategorySidebar
              priceRange={priceRange}
              setPriceRange={setPriceRange}
            />
          </aside>

          <main className="lg:w-3/4">
            <div className="mb-6 hidden rounded-xl bg-white p-4 shadow-sm sm:block">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="text-sm text-gray-600">
                  Showing {visibleProducts.length} of {sortedProducts.length} products
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-[#2E7B52] sm:w-auto"
                >
                  <option value="default">Default Sorting</option>
                  <option value="name">Name (A-Z)</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="discount">Highest Discount</option>
                </select>
              </div>
            </div>

            {products_loading || !products_loaded ? (
              <div className="rounded-2xl bg-white shadow-sm">
                <Loader label="Loading products" sublabel="Fetching fresh inventory and prices." />
              </div>
            ) : visibleProducts.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 text-primary-500">
                  <FaSearch className="h-8 w-8" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                  {visibleProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {visibleCount < sortedProducts.length && (
                  <div className="text-center mt-8">
                    <button
                      onClick={() => setVisibleCount(prev => prev + 12)}
                      className="w-full rounded-xl bg-primary-600 px-8 py-3 font-semibold text-white transition-all shadow-md hover:bg-primary-700 sm:w-auto"
                    >
                      Load More Products
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
